import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import QuotaExceededModal from "@/components/QuotaExceededModal";
import TrichoBetaCapture from "@/components/TrichoBetaCapture";
import CuteQuestionnaire, { CUTE_QUESTIONNAIRE_DEFAULTS } from "@/components/CuteQuestionnaire";
import { isTrichoBetaCaptureEnabled } from "@/lib/featureFlags";
import { ANALYSIS_SLOTS, SLOT_GROUPS } from "@/lib/analysisSlots";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ChevronLeft, Upload, X, Sparkles, Loader2, Camera, RefreshCw,
} from "lucide-react";

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error("Lettura file fallita"));
    reader.readAsDataURL(file);
  });
}

export default function NewAnalysis() {
  const { id } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [client, setClient] = useState(null);
  const [photos, setPhotos] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [quotaModal, setQuotaModal] = useState({ open: false, quota: null });
  const [followupStatus, setFollowupStatus] = useState(null);
  const [isFollowup, setIsFollowup] = useState(params.get("followup") === "1");
  const [earlyWarningOpen, setEarlyWarningOpen] = useState(false);
  const [trichoBetaOpen, setTrichoBetaOpen] = useState(false);
  // Questionario pre-analisi cute (INTERVENTO 3 — passo 3a).
  // Solo raccolta dati: NON collegato al motore. Tutte le risposte sono
  // opzionali (la cliente può deselezionarle); lo state è un dict piatto
  // { question_id: answer_value }, pre-compilato con gli stessi default
  // del pannello Survey di AITricoProtocol.html.
  const [cuteQuestionnaire, setCuteQuestionnaire] = useState(CUTE_QUESTIONNAIRE_DEFAULTS);
  const trichoBetaEnabled = isTrichoBetaCaptureEnabled();
  const fileRefs = useRef({});

  useEffect(() => {
    api.get(`/clients/${id}`)
      .then(res => setClient(res.data))
      .catch(() => { toast.error("Cliente non trovato"); nav("/"); });
    api.get(`/clients/${id}/followup-status`)
      .then(res => setFollowupStatus(res.data))
      .catch(() => {});
    // Pre-check quota so blocked users learn immediately
    api.get("/me/subscription")
      .then(res => {
        if (!res.data.allowed) {
          setQuotaModal({ open: true, quota: res.data });
        }
      }).catch(() => {});
  }, [id, nav]);

  const handleFile = async (slot, file) => {
    if (!file) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Solo JPG, PNG o WEBP sono supportati");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 8MB)");
      return;
    }
    try {
      // Foto inviata al backend SENZA modifiche: lo ScoreEngine deve
      // analizzare l'immagine così come caricata dall'utente. L'eventuale
      // ricompressione per lo storage (limite documento MongoDB) è
      // responsabilità esclusiva del backend, applicata DOPO l'analisi.
      const dataUrl = await readAsDataUrl(file);
      const base64 = dataUrl.split(",")[1];
      setPhotos(prev => ({
        ...prev,
        [slot.id]: { label: slot.label, data: base64, preview: dataUrl },
      }));
    } catch {
      toast.error("Immagine non leggibile. Riprova con un altro file.");
    }
  };

  const removePhoto = (slotId) => {
    setPhotos(prev => {
      const n = { ...prev };
      delete n[slotId];
      return n;
    });
  };

  // Ordine FISSO delle foto verso il backend: l'ordine di ANALYSIS_SLOTS,
  // NON l'ordine di caricamento (bianca → polarizzata → UV, zona vertex).
  const orderedPhotoList = () => ANALYSIS_SLOTS.map(s => photos[s.id]).filter(Boolean);

  const submit = async () => {
    // TricoProtocol richiede sempre la vista White Light (vertex-bianca):
    // è l'unica foto obbligatoria per /api/trico_scores lato motore di
    // scoring esterno.
    if (!photos["vertex-bianca"]) {
      toast.error("Carica la foto Vertex — Luce Bianca per procedere");
      return;
    }
    // Follow-up deve avere una baseline.
    if (isFollowup && followupStatus && followupStatus.reason === "no_baseline") {
      toast.error(followupStatus.message || "Nessuna analisi precedente disponibile.");
      return;
    }
    // Se il follow-up è anticipato (<30gg) chiediamo conferma esplicita.
    if (isFollowup && followupStatus && followupStatus.is_early) {
      setEarlyWarningOpen(true);
      return;
    }
    await actuallySubmit();
  };

  const actuallySubmit = async () => {
    const photoList = orderedPhotoList();
    // Guard simmetrico (anche actuallySubmit può essere invocato dal modal
    // early-warning, dove altrimenti mancherebbe questo controllo).
    if (!photos["vertex-bianca"]) {
      toast.error("Carica la foto Vertex — Luce Bianca per procedere");
      return;
    }
    // Difesa a monte: NON inviare mai foto con base64 vuoto/illeggibile.
    // Senza questa guardia il backend riceveva data="" e Anthropic
    // rispondeva "image cannot be empty" → 500 generico (fix backend
    // server.py la mappa ora a 400, ma evitiamo il round-trip inutile).
    const invalid = photoList.find(p => !p.data || typeof p.data !== "string" || p.data.length < 100);
    if (invalid) {
      toast.error(`La foto "${invalid.label}" non è leggibile. Ricaricala e riprova.`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        client_id: id,
        photos: photoList.map(p => ({ label: p.label, data: p.data })),
        is_followup: isFollowup,
      };
      // Questionario cute: lo includiamo solo se almeno una risposta è
      // stata data. Backend accetta anche `null`/assente.
      if (Object.keys(cuteQuestionnaire).length > 0) {
        payload.cute_questionnaire = cuteQuestionnaire;
      }
      // Flusso async (mag 2026): la POST crea l'analisi con status="processing"
      // e torna 202 con l'analysis_id in <1s. L'AI pipeline gira in
      // BackgroundTasks lato backend. Il frontend naviga subito alla pagina
      // di dettaglio con ?wait=1: quella pagina fa polling su
      // /api/analyses/{id}/status ogni 3s fino a completed|failed.
      // Timeout di 60s solo per la POST sincrona (se anche solo la creazione
      // del doc stub va oltre, c'è un problema di rete reale).
      const res = await api.post("/analyses", payload, { timeout: 60000 });
      const newId = res?.data?.id;
      if (!newId) throw new Error("ID analisi mancante nella risposta");
      toast.info("Analisi avviata. Stiamo elaborando le foto…");
      nav(`/analysis/${newId}?wait=1`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;

      // 🟡 ERRORE INPUT UTENTE
      if (status === 400) {
        if (typeof detail === "object" && detail?.error === "followup_no_baseline") {
          toast.error(detail.message || "Nessuna analisi precedente disponibile.");
        } else if (typeof detail === "string") {
          toast.error(detail);
        } else {
          toast.error("Dati non validi. Controlla le informazioni inserite.");
        }
        return;
      }

      // 🔴 QUOTA / CREDITO
      if (status === 402) {
        if (typeof detail === "object" && detail?.error === "quota_exceeded") {
          setQuotaModal({ open: true, quota: detail.quota });
          return;
        }
        toast.error(
          (typeof detail === "string" && detail) ||
          "Credito AI esaurito. Vai su Profilo per ricaricare."
        );
        return;
      }

      // 🔒 PIANO NON VALIDO
      if (status === 403) {
        toast.error("Funzione disponibile solo per piano PRO.");
        return;
      }

      // Cliente non trovato
      if (status === 404) {
        toast.error("Cliente non trovato. Ricarica la pagina.");
        return;
      }

      // 🔴 SERVER ERROR / TIMEOUT / NETWORK
      // NON sloggare, NON svuotare lo state: è solo la POST di creazione
      // che ha fallito. L'utente può semplicemente riprovare.
      if (status >= 500 || !status) {
        toast.error("Avvio analisi fallito. Riprova tra qualche istante.");
        return;
      }

      toast.error("Errore imprevisto. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <Link to={`/client/${id}`} className="inline-flex items-center gap-2 text-[#737373] hover:text-[#C5A059] transition-colors mb-8 group" data-testid="back-to-client">
        <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
        <span className="text-sm">Torna alla scheda</span>
      </Link>

      <div className="mb-12 animate-fade-up">
        <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">
          {isFollowup ? "Nuovo follow-up" : "Nuova analisi"}
        </p>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1A1A1A] mb-3">
          {isFollowup ? "Follow-up tricologico" : "Analisi tricologica AI"}
        </h1>
        {client && (
          <p className="text-[#737373]">
            Cliente: <span className="text-[#1A1A1A] font-medium">{client.name}</span>
          </p>
        )}

        {/* Toggle Follow-up — appare solo se esiste almeno una baseline */}
        {followupStatus && followupStatus.last_analysis_id && (
          <div
            className="mt-6 rounded-2xl border border-[#E5E5E0] bg-white p-5 flex flex-col sm:flex-row sm:items-center gap-4"
            data-testid="followup-toggle-card"
          >
            <div className="w-10 h-10 rounded-xl bg-[#FAF6EE] flex items-center justify-center shrink-0">
              <RefreshCw className="w-4 h-4 text-[#C5A059]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#1A1A1A]">
                Esegui come Follow-up
              </p>
              <p className="text-xs text-[#737373] mt-0.5">
                {followupStatus.reason === "no_baseline"
                  ? "Serve prima un'analisi standard per abilitare il follow-up."
                  : followupStatus.is_early
                    ? `Ultima analisi ${followupStatus.days_since_last} giorni fa — consigliato dopo 30. Ti chiederemo conferma prima di procedere.`
                    : `Ultima analisi ${followupStatus.days_since_last} giorni fa. Il follow-up confronta i progressi rispetto alla baseline.`}
              </p>
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="followup-toggle-label">
              <input
                type="checkbox"
                checked={isFollowup}
                disabled={followupStatus.reason === "no_baseline"}
                onChange={(e) => setIsFollowup(e.target.checked)}
                className="h-4 w-4 accent-[#C5A059] disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="followup-toggle-input"
              />
              <span className={`text-sm font-medium ${followupStatus.reason === "no_baseline" ? "text-[#A3A3A3]" : "text-[#1A1A1A]"}`}>
                {isFollowup ? "Attivo" : "Attiva"}
              </span>
            </label>
          </div>
        )}
      </div>

      {/* Banner Beta — Acquisizione da Tricocamera (dietro feature flag) */}
      {trichoBetaEnabled && (
        <div
          className="mb-6 rounded-2xl border border-[#C5A059]/30 bg-gradient-to-r from-[#C5A059]/5 to-transparent p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 animate-fade-up"
          data-testid="tricho-beta-banner"
        >
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-[#C5A059]/15 flex items-center justify-center flex-shrink-0">
              <Camera className="w-5 h-5 text-[#C5A059]" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-[#1A1A1A]">Acquisisci da Tricocamera</h3>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[#C5A059]/20 text-[#C5A059] font-semibold">
                  Beta
                </span>
              </div>
              <p className="text-sm text-[#737373] mt-1">
                Connessione WiFi diretta alla tricocamera, live video e scatti delle tre viste
                multiluce in un solo flusso guidato. Le foto compaiono automaticamente nei riquadri
                qui sotto.
              </p>
            </div>
          </div>
          <button
            onClick={() => setTrichoBetaOpen(true)}
            className="h-11 px-5 rounded-xl bg-[#1A1A1A] hover:bg-[#C5A059] text-white text-sm font-medium transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
            data-testid="tricho-beta-open-btn"
          >
            <Camera className="w-4 h-4" />
            Avvia acquisizione
          </button>
        </div>
      )}

      {/* Questionario pre-analisi cute (INTERVENTO 3 — passo 3a).
          Solo raccolta dati: le risposte vengono salvate insieme
          all'analisi ma NON influenzano ancora la scelta dei prodotti. */}
      <CuteQuestionnaire
        value={cuteQuestionnaire}
        onChange={setCuteQuestionnaire}
      />

      {/* Slot raggruppati: zona vertex in 3 luci. Ordine fisso, richiesto dal
          Pass 1 multiluce. */}
      {SLOT_GROUPS.map((group) => (
        <div key={group.key} className="mb-10">
          <div className="mb-4">
            <h2 className="font-serif-display text-2xl text-[#1A1A1A]">{group.title}</h2>
            <p className="text-sm text-[#737373] mt-0.5">{group.subtitle}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {ANALYSIS_SLOTS.filter(s => s.group === group.key).map((slot, idx) => {
              const photo = photos[slot.id];
              return (
                <div
                  key={slot.id}
                  className="rounded-2xl border border-[#E5E5E0] bg-white overflow-hidden animate-fade-up"
                  style={{ animationDelay: `${idx * 60}ms` }}
                  data-testid={`slot-${slot.id}`}
                >
                  <div className="aspect-[4/3] bg-[#FAFAFA] relative overflow-hidden">
                    {photo ? (
                      <>
                        <img src={photo.preview} alt={slot.label} className="w-full h-full object-cover" />
                        <button
                          onClick={() => removePhoto(slot.id)}
                          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 hover:bg-white shadow-md flex items-center justify-center transition-all"
                          data-testid={`remove-${slot.id}`}
                        >
                          <X className="w-4 h-4 text-[#1A1A1A]" />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => fileRefs.current[slot.id]?.click()}
                        className="absolute inset-0 flex flex-col items-center justify-center gap-3 hover:bg-[#EAE5DF]/40 transition-colors group"
                        data-testid={`upload-${slot.id}`}
                      >
                        <div className="w-14 h-14 rounded-2xl bg-white border border-[#E5E5E0] group-hover:border-[#C5A059] flex items-center justify-center transition-all">
                          <Camera className="w-6 h-6 text-[#C5A059]" />
                        </div>
                        <span className="text-sm text-[#737373]">Carica foto</span>
                      </button>
                    )}
                    <input
                      ref={(el) => (fileRefs.current[slot.id] = el)}
                      type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                      onChange={(e) => handleFile(slot, e.target.files?.[0])}
                      data-testid={`file-input-${slot.id}`}
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-serif-display text-xl text-[#1A1A1A]">{slot.label}</h3>
                    <p className="text-xs text-[#737373] mt-1">{slot.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* CTA */}
      <div className="rounded-2xl border border-[#E5E5E0] bg-white p-6 lg:p-8 flex items-center justify-end gap-5">
        <button
          onClick={submit}
          disabled={!photos["vertex-bianca"] || submitting}
          className="h-14 px-8 rounded-xl bg-[#1A1A1A] hover:bg-[#C5A059] text-white font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-3 min-w-[220px]"
          data-testid="submit-analysis-button"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {isFollowup ? "Follow-up in corso..." : "Analisi in corso..."}
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5" />
              {isFollowup ? "Avvia Follow-up" : "Avvia analisi AI"}
            </>
          )}
        </button>
      </div>
      <QuotaExceededModal
        open={quotaModal.open}
        onOpenChange={(v) => setQuotaModal({ open: v, quota: v ? quotaModal.quota : null })}
        quota={quotaModal.quota}
      />

      <AlertDialog open={earlyWarningOpen} onOpenChange={setEarlyWarningOpen}>
        <AlertDialogContent data-testid="followup-early-warning">
          <AlertDialogHeader>
            <AlertDialogTitle>Follow-up anticipato</AlertDialogTitle>
            <AlertDialogDescription>
              Il follow-up è consigliato dopo 30 giorni. Vuoi comunque procedere?
              {followupStatus?.days_since_last != null && (
                <span className="block mt-2 text-[#737373]">
                  Ultima analisi: {followupStatus.days_since_last}{" "}
                  {followupStatus.days_since_last === 1 ? "giorno" : "giorni"} fa.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="followup-warning-cancel">
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                setEarlyWarningOpen(false);
                await actuallySubmit();
              }}
              data-testid="followup-warning-proceed"
            >
              Procedi comunque
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal Beta — Acquisizione da Tricocamera (dietro feature flag) */}
      {trichoBetaEnabled && (
        <TrichoBetaCapture
          open={trichoBetaOpen}
          onClose={(reason) => {
            setTrichoBetaOpen(false);
            if (reason === "error") {
              toast.info("Carica le foto da disco per continuare l'analisi");
            }
          }}
          onPhotosReady={(captured) => {
            // Le foto Beta hanno la stessa identica forma usata da handleFile.
            // Le iniettiamo nello state `photos` come se fossero arrivate dal
            // pulsante "Carica foto". Da qui in poi il flusso "Avvia Analisi"
            // è invariato.
            setPhotos((prev) => ({ ...prev, ...captured }));
            const n = Object.keys(captured || {}).length;
            if (n > 0) toast.success(`${n} foto acquisite dalla tricocamera`);
          }}
        />
      )}
    </Layout>
  );
}
